import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { useCustomer } from '@/hooks/useCustomer';

export interface CylinderAuditDue {
  label: string;
  alerted_at: string;
}

/**
 * Is this account on the cylinder audit list? Reads
 * orders.fn_my_cylinder_audit_due() — a definer function that takes NO
 * argument, so it can only ever answer for the signed-in customer. The alerts
 * table itself is service-role only; this is the one customer-facing window
 * onto it, and it returns labels and dates, never the unbilled rent.
 *
 * An open row means: our books and the tanks on site disagree, and the next
 * delivery carries a count (the driver's job carries the same instruction).
 * The row clears itself when the audit is filed, so the banner needs no
 * dismissal — it goes away when the thing it is about is done.
 */
export function useCylinderAuditDue() {
  const { data: ctx } = useCustomer();
  const customerId = ctx?.customer.id ?? null;
  return useQuery<CylinderAuditDue[]>({
    queryKey: ['cylinder-audit-due', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const { data, error } = await supabase.schema('orders').rpc('fn_my_cylinder_audit_due');
      if (error) throw new Error(error.message);
      return (data ?? []) as CylinderAuditDue[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
